import { create } from 'zustand';
import type { LoginInput } from '@eombor/contracts';
import { createApiClient } from '../../../shared/api/client.js';
import { getEnv } from '../../../shared/config/env.js';
import { createAuthApi, type AuthTokensDto, type SessionUser } from '../api/auth.api.js';
import type { GrantedPermissionDto } from './permission.types.js';

/**
 * Tokenlar **`sessionStorage`** da turadi, `localStorage` da emas: tab yopilishi
 * bilan sessiya ham yo'qoladi va XSS uchun oyna qisqaroq bo'ladi. Refresh token
 * `httpOnly` cookie ga o'tishi 2-etapda rejalashtirilgan.
 */
export const AUTH_STORAGE_KEY = 'eombor:auth';

/** Saqlanadigan qism — ruxsatlar saqlanmaydi, ular har safar serverdan olinadi. */
interface StoredSession {
  accessToken: string;
  refreshToken: string;
  user: SessionUser;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  permissions: GrantedPermissionDto[];
  login: (input: LoginInput) => Promise<void>;
  /**
   * Tayyor tokenlar bilan seans ochish — Telegram orqali kirish uchun.
   *
   * `login` dan farqi: parol yo'q. Tokenlarni server bergan bo'ladi
   * (`POST /auth/telegram/status`), ya'ni xodim allaqachon isbotlangan.
   */
  loginWithTokens: (tokens: AuthTokensDto) => Promise<void>;
  /**
   * Sessiyadagi foydalanuvchi maydonlarini yangilaydi (tokenlarga tegmasdan).
   *
   * Parol almashtirilgandan keyin kerak: `mustChangePassword` tushadi va
   * to'siq ochiladi. Saqlangan sessiyaga ham yoziladi — aks holda sahifa
   * yangilanganda eski bayroq qaytib kelib, to'siq yana yopilardi.
   */
  setUser: (user: SessionUser) => void;
  logout: () => Promise<void>;
  /**
   * Access token eskirganda YANGISINI oladi. `true` — muvaffaqiyat.
   *
   * API klienti buni 401 javobida chaqiradi. Busiz har 15 daqiqada (access
   * token muddati) tizim foydalanuvchini kirish sahifasiga otib yuborardi,
   * garchi qo'lida 7 kunlik refresh token tursa ham.
   */
  renewSession: () => Promise<boolean>;
  hydrate: () => void;
  /** Serverga murojaat qilmasdan lokal sessiyani o'chiradi (401 javobida). */
  clearSession: () => void;
  /**
   * Kirishda TERILGAN parol — faqat vaqtinchalik kalit uchun va faqat
   * XOTIRADA.
   *
   * Majburiy almashtirish oynasida "Joriy parol" maydoniga o'zi yoziladi:
   * odam Telegramdan olgan olti xonali kodni ikkinchi marta terib
   * o'tirmasin. Saqlashga (`sessionStorage`) YOZILMAYDI va parol
   * almashtirilgan zahoti o'chadi — brauzerda qolib ketadigan parol
   * kalitning o'zidan xavfliroq bo'lardi.
   */
  pendingPassword: string | null;
}

const readStored = (): StoredSession | null => {
  // Buzilgan yoki begona JSON butun ilovani yiqitmasligi kerak — kirish
  // sahifasi baribir ochilishi shart.
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { accessToken, refreshToken, user } = parsed as Partial<StoredSession>;
    if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return null;
    if (typeof user !== 'object' || user === null || typeof user.id !== 'string') return null;
    return { accessToken, refreshToken, user };
  } catch {
    return null;
  }
};

const writeStored = (session: StoredSession): void => {
  try {
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Xotira to'lgan yoki saqlash taqiqlangan — sessiya faqat shu sahifa
    // ochiq turguncha ishlaydi, bu kirishga to'sqinlik qilmaydi.
  }
};

const clearStored = (): void => {
  try {
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Yuqoridagi bilan bir xil sabab.
  }
};

const EMPTY: Pick<
  AuthState,
  'accessToken' | 'refreshToken' | 'user' | 'permissions' | 'pendingPassword'
> = {
  accessToken: null,
  refreshToken: null,
  user: null,
  permissions: [],
  pendingPassword: null,
};

/**
 * Ilovaning yagona API klienti. U shu yerda quriladi, chunki `Authorization`
 * sarlavhasi uchun kerak bo'lgan token aynan shu do'konda turadi — `getToken`
 * do'konning o'zidan o'qigani uchun modullar orasida aylanma bog'liqlik yo'q.
 */
export const apiClient = createApiClient({
  baseUrl: getEnv().apiBaseUrl,
  getToken: () => useAuthStore.getState().accessToken,
  // 401 — avval TOKENNI YANGILAB ko'ramiz: ko'p hollarda u shunchaki
  // eskirgan bo'ladi va foydalanuvchi buni sezmasligi kerak.
  renewSession: () => useAuthStore.getState().renewSession(),
  // Yangilash ham yordam bermadi — faqat shundagina lokal holat tozalanadi.
  // `logout()` chaqirilsa, uning o'zi ham 401 olishi va cheksiz halqa hosil
  // qilishi mumkin edi.
  onUnauthorized: () => {
    useAuthStore.getState().clearSession();
  },
});

export const authApi = createAuthApi(apiClient);

export const useAuthStore = create<AuthState>()((set, get) => {
  const applySession = (tokens: AuthTokensDto): void => {
    const session: StoredSession = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user: tokens.user,
    };
    writeStored(session);
    set({ ...session, permissions: [] });
  };

  /**
   * Ruxsatlar UI ko'rinishini boshqaradi, kirishning shartini emas — shuning
   * uchun `/auth/me` yiqilsa kirish baribir davom etadi, ro'yxat esa bo'sh
   * qoladi ("fail closed": ortiqcha tugma chizilmaydi).
   */
  const loadPermissions = async (): Promise<void> => {
    try {
      const { permissions, ...user } = await authApi.me();
      set({ permissions, user });
    } catch {
      set({ permissions: [] });
    }
  };

  return {
    ...EMPTY,

    login: async (input) => {
      const session = await authApi.login(input);
      applySession(session);
      /*
       * Vaqtinchalik kalit bilan kirdi — uni majburiy almashtirish oynasiga
       * uzatib qo'yamiz. Boshqa hollarda saqlanmaydi: oddiy parolni xotirada
       * ushlab turishning hech qanday foydasi yo'q.
       */
      if (session.user.mustChangePassword) set({ pendingPassword: input.password });
      await loadPermissions();
    },

    loginWithTokens: async (tokens) => {
      applySession(tokens);
      await loadPermissions();
    },

    setUser: (user) => {
      const { accessToken, refreshToken } = get();
      if (accessToken !== null && refreshToken !== null) {
        writeStored({ accessToken, refreshToken, user });
      }
      // Parol almashtirildi — vaqtinchalik kalit endi keraksiz va zararli.
      set({ user, ...(user.mustChangePassword ? {} : { pendingPassword: null }) });
    },

    logout: async () => {
      const token = get().refreshToken;
      try {
        if (token !== null) await authApi.logout(token);
      } catch {
        // Server javob bermasa ham lokal sessiya tozalanadi — foydalanuvchi
        // "chiqdim" deb o'ylab, tokeni brauzerda qolib ketmasin.
      }
      get().clearSession();
    },

    renewSession: async () => {
      const token = get().refreshToken;
      if (token === null) return false;
      try {
        const session = await authApi.refresh(token);
        writeStored({
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          user: session.user,
        });
        /*
         * Ruxsatlar TEGILMAYDI. `applySession` ularni bo'shatib yuborardi va
         * jimgina yangilash paytida ekrandagi tugmalar bir zumga g'oyib
         * bo'lardi — foydalanuvchi uchun bu xuddi huquqi olinganday
         * ko'rinardi.
         */
        set({
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          user: session.user,
        });
        return true;
      } catch {
        return false;
      }
    },

    hydrate: () => {
      const stored = readStored();
      if (stored === null) {
        set({ ...EMPTY });
        return;
      }
      set({ ...stored, permissions: [], pendingPassword: null });
      // Ruxsatlar saqlanmaydi — sahifa yangilangach qaytadan olinadi.
      void loadPermissions();
    },

    clearSession: () => {
      clearStored();
      set({ ...EMPTY });
    },
  };
});
